import React, { useCallback, useEffect, useState } from "react";
import { Clock, Eye, Forward, Mail, MailCheck, Paperclip } from "lucide-react";
import { api } from "./api";
import { Badge, Carte, EnTeteMenu, Modale, Puces, Recherche } from "./ui";

/* Messages → « E-mails envoyés » (Jen, 31.10.2026 : « tous les e-mails envoyés via Resend,
   avec une recherche et l'aperçu »). Tout ce qui part du site et de la console :
   confirmations, factures, rappels, billets, cours privés, envois groupés… Toucher une
   ligne ouvre l'e-mail tel que la personne l'a reçu. Gardés 180 jours.
   « Transférer » (Jen, 02.11.2026 : « si on a un e-mail erroné, j'aimerais pouvoir forwarder le
   message facilement à la nouvelle adresse ») : le même e-mail repart à l'adresse tapée. */

const STATUTS = { envoye: ["Envoyé", "vert"], refuse: ["Refusé", "rouge"], simule: ["Simulé (test)", "neutre"] };
const PERIODES = [[7, "7 jours"], [30, "30 jours"], [90, "3 mois"], [180, "6 mois"]];
const quand = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function EmailsEnvoyes() {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [q, setQ] = useState("");
  const [statut, setStatut] = useState("");
  const [jours, setJours] = useState(30);
  const [ouvert, setOuvert] = useState(null);
  const charger = useCallback(() => {
    const p = new URLSearchParams({ q, statut, jours: String(jours) });
    api(`/emails-envoyes?${p}`).then(setD).catch((e) => setErreur(e.message));
  }, [q, statut, jours]);
  useEffect(() => { const t = setTimeout(charger, q ? 250 : 0); return () => clearTimeout(t); }, [charger, q]);
  const ouvrir = (m) => { setOuvert({ ...m, html: null }); api(`/emails-envoyes/${m.id}`).then(setOuvert).catch((e) => setOuvert({ ...m, erreur: e.message })); };

  const c = d?.comptes || { envoye: 0, refuse: 0, simule: 0 };
  return (
    <div className="space-y-5" data-testid="console-emails-envoyes">
      <EnTeteMenu ton="messages" Icon={MailCheck} contexte="Site et console" titre="E-mails envoyés" testid="emails-envoyes-entete"
        points={[
          [Mail, "Tout ce qui part", "Confirmations, factures, rappels, billets, envois"],
          [Eye, "Relire", "Touche une ligne : l'e-mail tel qu'il a été reçu"],
          [Forward, "Transférer", "Adresse fausse ? Renvoie-le à la bonne"],
          [Clock, "Gardés", "6 mois"],
        ]} />
      <div className="grid grid-cols-3 gap-3" data-testid="emails-envoyes-chiffres">
        <Carte titre="Envoyés" valeur={c.envoye} detail={`sur ${PERIODES.find(([n]) => n === jours)?.[1] || `${jours} jours`}`} ton="vert" onClick={() => setStatut("envoye")} />
        <Carte titre="Refusés" valeur={c.refuse} detail="adresse invalide ou refus de Resend" ton={c.refuse ? "rouge" : "neutre"} onClick={() => setStatut("refuse")} />
        <Carte titre="Simulés" valeur={c.simule} detail="version de test : rien n'est parti" onClick={() => setStatut("simule")} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Puces valeur={statut} onChange={setStatut} testid="emails-envoyes-statut" options={[
          { valeur: "", label: "Tous" }, { valeur: "envoye", label: "Envoyés" },
          { valeur: "refuse", label: "Refusés" }, { valeur: "simule", label: "Simulés" }]} />
        <select className="input-field !py-1.5 text-sm" style={{ width: 130 }} value={jours} onChange={(e) => setJours(Number(e.target.value))} data-testid="emails-envoyes-periode">
          {PERIODES.map(([n, l]) => <option key={n} value={n}>{l}</option>)}
        </select>
        <span className="ml-auto"><Recherche valeur={q} onChange={setQ} placeholder="Adresse, sujet, nom…" testid="emails-envoyes-recherche" largeur={280} /></span>
      </div>
      {erreur && <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>}
      <div className="bg-white rounded-2xl border overflow-x-auto" style={{ borderColor: "var(--border)" }} data-testid="emails-envoyes-liste">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-[var(--muted)] border-b" style={{ borderColor: "var(--border)" }}>
              <th className="px-3 py-2 font-semibold">Quand</th><th className="px-3 py-2 font-semibold">À</th>
              <th className="px-3 py-2 font-semibold">Sujet</th><th className="px-3 py-2 font-semibold">État</th>
            </tr>
          </thead>
          <tbody>
            {(d?.items || []).map((m) => {
              const [label, ton] = STATUTS[m.statut] || STATUTS.simule;
              return (
                <tr key={m.id} onClick={() => ouvrir(m)} className="border-b last:border-b-0 cursor-pointer hover:bg-[var(--primary-tint)]" style={{ borderColor: "var(--border-soft)" }} data-testid="email-envoye">
                  <td className="px-3 py-2 whitespace-nowrap text-[var(--muted)] text-xs">{quand(m.le)}</td>
                  <td className="px-3 py-2 max-w-[16rem] truncate">{(m.a || []).join(", ")}{m.cc?.length ? <span className="text-[var(--muted)] text-xs"> · cc {m.cc.join(", ")}</span> : null}</td>
                  <td className="px-3 py-2">{m.sujet}{m.pieces?.length ? <span className="text-[var(--muted)] text-xs inline-flex items-center gap-0.5 ml-1.5"><Paperclip size={11} />{m.pieces.length}</span> : null}</td>
                  <td className="px-3 py-2 whitespace-nowrap"><Badge ton={ton}>{label}</Badge></td>
                </tr>
              );
            })}
            {d && !d.items.length && <tr><td colSpan={4} className="px-3 py-8 text-center text-[var(--muted)]">Aucun e-mail{q ? " pour cette recherche" : ""}.</td></tr>}
            {!d && !erreur && <tr><td colSpan={4} className="px-3 py-8 text-center text-[var(--muted)]">Chargement…</td></tr>}
          </tbody>
        </table>
      </div>
      {ouvert && (
        <Modale large titre={ouvert.sujet} onFermer={() => setOuvert(null)} testid="email-envoye-apercu"
          sous_titre={`${quand(ouvert.le)} · à ${(ouvert.a || []).join(", ")}${ouvert.cc?.length ? ` · cc ${ouvert.cc.join(", ")}` : ""} · ${(STATUTS[ouvert.statut] || STATUTS.simule)[0]}`}>
          {ouvert.pieces?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">{ouvert.pieces.map((p) => <Badge key={p} ton="neutre"><Paperclip size={11} /> {p}</Badge>)}</div>
          )}
          {ouvert.erreur ? <p className="text-sm" style={{ color: "#B91C1C" }}>{ouvert.erreur}</p>
            : ouvert.html == null ? <p className="text-sm text-[var(--muted)]">Chargement…</p>
            : <iframe title="Aperçu de l'e-mail" sandbox="" srcDoc={ouvert.html} className="w-full rounded-xl border" style={{ height: "65vh", borderColor: "var(--border)" }} data-testid="email-envoye-contenu" />}
          {ouvert.tronque && <p className="text-xs text-[var(--muted)] mt-2">E-mail très long : seul le début est gardé.</p>}
          {ouvert.html != null && !ouvert.erreur && !ouvert.tronque && <Transferer key={ouvert.id} mail={ouvert} onFait={charger} />}
        </Modale>
      )}
    </div>
  );
}

/* « Transférer » : le même e-mail (sujet et contenu) repart à une autre adresse. */
function Transferer({ mail, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const [email, setEmail] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [retour, setRetour] = useState(null);
  const envoyer = async (e) => {
    e.preventDefault();
    setEnvoi(true); setRetour(null);
    try {
      const r = await api(`/emails-envoyes/${mail.id}/transferer`, { method: "POST", body: JSON.stringify({ email: email.trim() }) });
      setRetour({ ok: true, texte: r.message }); setOuvert(false); setEmail(""); onFait?.();
    } catch (err) { setRetour({ ok: false, texte: err.message }); }
    setEnvoi(false);
  };
  return (
    <div className="mt-3" data-testid="email-transferer">
      {retour && <p className="text-sm mb-2" style={{ color: retour.ok ? "#15803D" : "#B91C1C" }} data-testid="email-transferer-retour">{retour.texte}</p>}
      {!ouvert ? (
        <button type="button" className="btn-ghost text-sm inline-flex items-center gap-1.5" onClick={() => setOuvert(true)} data-testid="email-transferer-ouvrir">
          <Forward size={15} /> Transférer à une autre adresse
        </button>
      ) : (
        <form onSubmit={envoyer} className="rounded-xl p-3 space-y-2" style={{ background: "#F8FAFC", border: "1px solid var(--border)" }}>
          <label className="block text-sm font-medium" htmlFor="email-transferer-adresse">À quelle adresse ?</label>
          <div className="flex flex-wrap items-center gap-2">
            <input id="email-transferer-adresse" type="email" required autoFocus className="input-field flex-1 min-w-[220px]" placeholder="nouvelle.adresse@exemple.ch"
              value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-transferer-adresse" />
            <button type="submit" className="btn-primary text-sm" disabled={envoi || !email.trim()} data-testid="email-transferer-envoyer">{envoi ? "Envoi…" : "Envoyer"}</button>
            <button type="button" className="btn-ghost text-sm" disabled={envoi} onClick={() => setOuvert(false)}>Annuler</button>
          </div>
          <p className="text-xs text-[var(--muted)] leading-snug">
            Le même e-mail repart tel quel (même sujet, même contenu).
            {mail.pieces?.length ? " Les pièces jointes ne partent pas : une facture se renvoie depuis Facturation." : ""}
            {" "}Si l'adresse était fausse, corrige-la aussi dans la fiche de la famille : sinon les prochains e-mails repartiront à l'ancienne.
          </p>
        </form>
      )}
    </div>
  );
}
